import math from '../../../services/expression';

import { parseMacroAndSendToChat, sendToChat, parseControlFunctions } from '../../TemplateBuilder/SheetBuilder/Util/MacroParser';
import { copyToClipboard, pasteFromClipboard } from '../../../services/clipboard'
import { matchRecursive, resolveTreeExpressions, expandTreeExpressions } from '../../../services/matchRecursive';

import FieldTools from '../FieldTools';
import TextField from './Fields/TextField';
import TextSoloField from './Fields/TextSoloField';
import Label from './Fields/Label';
import TextInt from './Fields/TextInt';
import TextFloat from './Fields/TextFloat';
import TextArea from './Fields/TextArea';
import Select from './Fields/Select';
import Checkbox from './Fields/Checkbox';
import Group from './Fields/Group';
import Image from './Fields/Image';

const fieldClasses = {TextField, TextSoloField, Label, TextInt, TextFloat, TextArea, Select, Checkbox, Group, Image};

export default class PageManager {

    constructor() {
        this.pageRows = null;
        this.pageCols = null;
        this.tool = null;
        this.currentField = null;
        this.template = {page1:[]};
        this.currentCell = [0, 0];
        this.page = 1;
        this.startCell = [0, 0];
        this.endCell = [0, 0];
        this.offesetCell = [0, 0];
        this.mouseState = [0,0];
        this.emUnit = 16;
        this.fieldProps = {};
        this.renderTarget = null;
        this.selectedItem = null;
        this.clipboard = null;
        this.detailState = [false, null];
        this.values = {};
        this.pages = 1;
        this.onSelect = () => {};
        this.onFieldChange = () => {};
        this.onAfterMacroRoll = () => {};
        this.onModifyContent = () => {};
        this.exported = () => {};
        this.pageChangeCallback = () => {};
    }

    setRows(rows) {
        this.pageRows = rows;
    }

    setCols(cols) {
        this.pageCols = cols;
    }

    setTool(tool) {
        this.tool = tool;
        if (this.tool !== null) {
            this.selectedItem = null;
            this.render();
        }
    }

    setFieldProps(props) {
        this.fieldProps = props;
        
        let options = this.factoryItemOptions();
        let index = 0;

        for(let page in this.template) {
            const pageNum = parseInt(page.substring(4)) - 1;
            for (let item of this.template[page]) {
                if (item.id === this.selectedItem) {
                    if (props.type && item.type !== props.type) {
                        let newProps = {...(item.export()), type: props.type};
                        let newItem = this.factoryField(newProps, pageNum);

                        item.remove();
                        this.template[page].splice(index, 1, newItem);
                        this.render();
                    } else {
                        item.setProps(this.fieldProps);
                        item.update(options);
                    }
                    break;
                }
                index++;
            }
        };

        let minPages = this.getMinNumPages();
        
        this.onModifyContent({minPages});
    }

    setRenderTarget(target) {
        this.renderTarget = target;
    }

    setOnSelect(fn) {
        this.onSelect = fn;
    }

    setPageChangeCallback(fn) {
        this.pageChangeCallback = fn;
    }

    setOnModifyContent(fn) {
        this.onModifyContent = fn;
    }

    setExport(fn) {
        this.exported = fn;
    }

    setFieldValueChange(fn) {
        this.onFieldChange = fn;
    }

    setAfterMacroRoll(fn) {
        this.onAfterMacroRoll = fn;
    }

    setDetailState(opened, detailId) {
        this.detailState = [opened, detailId];
    }

    setNumPages(p) {
        this.pages = p;
        this.exported(this.export());
    }

    getDetailData() {
        return {
            'value': this.values[`${this.detailState[1]}`] || '',
            'description': this.values[`${this.detailState[1]}_description`] || '',
            'macro': this.values[`${this.detailState[1]}_macro`] || '',
        };
    }

    getDetailValue(key) {
        if (key === 'value') {
            return this.values[`${this.detailState[1]}`] || '';
        }
        return this.values[`${this.detailState[1]}_${key}`] || '';
    }

    static getNumberOfPages(content) {
        if (content.length >= 1) {
            return 1;
        } else {
            let pages = 0;
            let limit = 5;

            while (content['page'+(pages+1)] && limit--) {
                pages++;
            }

            return Math.max(pages,1);
        }
    }

    syncTemplatePages(pages) {
        if (!pages) {
            return;
        }

        let numPages = PageManager.getNumberOfPages(this.template);
        if (numPages > pages) {
            for(let i=numPages; i>pages; i--) {
                if (this.template['page'+i] && !this.template['page'+i].length) {
                    delete this.template['page'+i];
                }
            }
        } else {
            for(let i=numPages+1; i<=pages; i++) {
                this.template['page'+i] = [];
            }
        }
    }

    setDetailValue(key, value) {
        if (key === 'value') {
            this.values[`${this.detailState[1]}`] = value;

            if (this.template.length) {
                for (let field of this.template) {
                    if (this.detailState[1] === (field.name || field.nameId)) {
                        if (field.get() !== value) {
                            field.set(typeof value === 'undefined' ? (field.default || '') : value);
                        }
                        break;
                    }
                }
            } else {
                for (let p in this.template) {
                    for (let field of this.template[p]) {
                        if (this.detailState[1] === (field.name || field.nameId)) {
                            if (field.get() !== value) {
                                field.set(typeof value === 'undefined' ? (field.default || '') : value);
                            }
                            break;
                        }
                    }
                }
            }
        } else {
            this.values[`${this.detailState[1]}_${key}`] = value;
        }

        return this.getDetailData();
    }

    persistDetail() {
        this.onFieldChange(this.values);
    }

    sendDetailToChat() {
        let description = this.values[`${this.detailState[1]}_description`];
        let title = this.values[`${this.detailState[1]}`];

        if (!description || !title) {
            return;
        }

        sendToChat(title, description.replace(/\n/gim,"\\n"), () => {
            this.onAfterMacroRoll();
        });
    }

    rollDetail() {
        let macro = this.values[`${this.detailState[1]}_macro`];
        if (!macro) {
            return;
        }

        let sheet = this.getValues();
        
        parseMacroAndSendToChat(macro, sheet, () => {
            this.onAfterMacroRoll();
        });
    }

    sanitizeExpression(exp) {
        return exp.replace('evaluate','').replace('eval','').replace('parse','');
    }

    getFieldFunctionOf(field) {
        let reg = new RegExp("\\[(\\s*[a-zA-Z0-9_]+\\s*)\\]", 'g');
        let expression = this.sanitizeExpression(field.props.expression);
        let refs = String(expression).match(reg);

        if (!refs) {
            return [];
        }

        return refs.map(i => i.replace(/\[\s*/g,'').replace(/\s*\]/g,''));
    }

    parseFieldExpression(field, sheet) {
        let expression = field.props.expression;
        let key = field.name || field.nameId;

        try {
            let parts = [...matchRecursive(expression, "[...]")];
            let expressionsTree = expandTreeExpressions(parts, sheet);

            
            expression = resolveTreeExpressions(expressionsTree, expression, false);
            expression = parseControlFunctions(expression);

            sheet[key] = math.evaluate(expression, sheet);

            if (field.elements.input && field.elements.input.internalType === 'TextInt') {
                sheet[key] = String(parseInt(sheet[key]));
            }
            field.fix();
        } catch (e) {
            sheet[key] = '';
            console.log(Object.assign(e,{component:'PageManager', id: 'SheetEvaluateExpression'}));
            field.error();
        }
    }

    clearClipboard() {
        this.clipboard = null;
    }

    toClipboard(e) {
        e.preventDefault();
        if (this.template.length) {
            for (let field of this.template) {
                if (this.selectedItem === field.id) {
                    this.clipboard = [field.export()];
                    copyToClipboard(JSON.stringify(this.clipboard));
                }
            }
        } else {
            for (let p in this.template) {
                for (let field of this.template[p]) {
                    if (this.selectedItem === field.id) {
                        this.clipboard = [field.export()];
                        copyToClipboard(JSON.stringify(this.clipboard));
                    }
                }
            }
        }
    }

    async fromClipboard(e) {
        e.preventDefault();
        let clip = await pasteFromClipboard();
        
        try {
            let fields = JSON.parse(clip);
            if (fields.length) {
                for (let f of fields) {
                    if (!f.type || !f.id) {
                        continue;
                    }

                    f.id = (new Date()).getTime() + '' + (Math.random() * 8999 + 1000);
                    this.template['page'+(this.page+1)].push(this.factoryField(f, this.page));
                    this.selectedItem = f.id;
                }
            }
        } catch (e) {
            if (this.clipboard) {
                this.clipboard.id = (new Date()).getTime();
                this.template['page'+(this.page+1)].push(this.factoryField(this.clipboard, this.page));
                this.selectedItem = this.clipboard.id;
            }
        }

        this.render();
    }

    buildSheet(skipUpdate = false) {
        let sheet = this.values;
        let doneFields = [];
        let parseableFields = {};

        for (let p in this.template) {
            for (let field of this.template[p]) {
                let key = field.name || field.nameId;
                let element = document.querySelector(`[name=sheet-${key}]`);

                if (field.type === 'Checkbox') {
                    if (typeof this.values[key] === 'undefined') {
                        element.checked = field.defaultValue === '1';
                        sheet[key] = field.defaultValue === '1';
                    }
                    sheet[key] = element && element.checked ? 1 : 0;
                } else {
                    sheet[key] = element ? (element.value || field.defaultValue) : (field.defaultValue || '');
                }
                

                if (field.props.auto) {
                    parseableFields[key] = field;
                } else {
                    doneFields.push(key);
                }
            }
        }

        let parseablesKeys = Object.keys(parseableFields);

        let prevent = 0;
        while (parseablesKeys.length > 0) {
            prevent++;
            if (prevent > 1000) {
                Object.assign(new Error('Infinity Loop parsing sheet'),{component:'PageManager', id: 'BuildSheetMethodLoop'})
                break;
            } 


            for (let f of parseablesKeys) {
                let functionOf = this.getFieldFunctionOf(parseableFields[f]);
                if (!functionOf.some(i => doneFields.indexOf(i) < 0)) {
                    this.parseFieldExpression(parseableFields[f], sheet);
                    prevent = 0;
                    doneFields.push(f);
                    delete parseableFields[f];
                }
            }
            parseablesKeys = Object.keys(parseableFields);
        }

        this.setValues(sheet);
        if (!skipUpdate) {
            this.onFieldChange(sheet);
        }
    }

    setValues(values) {
        this.values = values;
        for (let p in this.template) {
            let fields = this.template[p];

            for (let field of fields) {
                let val = values[field.name || field.nameId];
                if (field.get() !== val) {
                    field.set(typeof val === 'undefined' ? (field.default || '') : val);
                }
            }
        }
    }

    getValues() {
        return this.values;
    }

    clearValues() {
        for(let p in this.template) { 
            let fields = this.template[p];
            for (let field of fields) {
                field.clean();
            }
        }
    }

    propagateSelection() {
        this.onSelect(this.selectedItem, this.fieldProps);
    }

    resize() {
        let size = document.querySelector('.content-render').getBoundingClientRect();
        this.emUnit = Math.round(size.width/this.pageCols);
        this.render();
    }

    removeSelected() {
        if (this.template.length) {
            this.template = this.template.filter(i => {
                if (i.id !== this.selectedItem) {
                    return true;
                }

                i.remove();
                return false;
            });
        } else {
            for(let i=1; i<=this.pages; i++) {
                this.template[`page${i}`] = this.template[`page${i}`].filter(i => {
                    if (i.id !== this.selectedItem) {
                        return true;
                    }


                    i.remove();
                    return false;
                });
            }
        }
        this.selectedItem = null;
    }

    factoryItemOptions() {
        return {
            unit: this.emUnit,
            selectedId: this.selectedItem
        };
    }

    seekItemToSelect(col, row, page) {
        let selectedItemCandidate = null;
        this.selectedItem = null;
        this.fieldProps = {};

        for(let i=this.template['page' + (page+1)].length-1; i>=0; i--) {
            let item = this.template['page' + (page+1)][i];
            if (col >= item.x && col < (item.x+item.width) && 
                row >= item.y && row < (item.y+item.height)) {
                    if (item.type === 'Group') {
                        selectedItemCandidate = [item.id, item.getProps()];
                    } else {
                        this.selectedItem = item.id;
                        this.fieldProps = item.getProps();
                        break;
                    }
            }
        }

        if (this.selectedItem === null && selectedItemCandidate !== null) {
            this.selectedItem = selectedItemCandidate[0];
            this.fieldProps = selectedItemCandidate[1];
        }

        this.propagateSelection();
        this.render();
    }

    getSelectedItem(page) {
        let tmpl = this.template;

        if (this.template && this.template['page' + (page+1)]) {
            tmpl = this.template['page' + (page+1)];
        }

        for (let item of tmpl) {
            if (item.id === this.selectedItem) {
                return item;
            }
        }
    }

    factoryField(item, page) {
        let field = new fieldClasses[item.type](item.startPoint);
        field.setManager(this);
        field.setId(item.id);
        field.setName(item.name);
        field.setNameId(item.nameId);
        field.setDefaultValue(item.defaultValue);
        field.setOptions(item.options);
        field.setStartPoint(item.startPoint);
        field.setEndPoint(item.endPoint);
        field.setProps(item.props);
        field.normalize();
        field.setRunMacroCallback(()=>this.onAfterMacroRoll());
        if (this.renderTarget[page || 0]) {
            field.create(this.renderTarget[page || 0], {onBlur: () => {this.buildSheet()}, unit: this.emUnit});
        }

        return field;
    }

    getMinNumPages() {
        if (this.template.length >= 0) {
            return 1;
        }

        const pagesToCheck = Object.values(this.template).reverse();
        let minNumPages = pagesToCheck.length;

        for (let p of pagesToCheck) {
            if (p.length > 0) {
                break;
            }
            minNumPages--;
        }

        return minNumPages;
    }

    export() {
        let result = {};

        for(let i=1; i<=this.pages; i++) {
            result['page'+i] = [];
        }
        
        if (this.template.length) {
            result['page1'] = this.template.map(i => i.export());
        } else {
            let numPages = Object.keys(this.template).length;
            if (numPages) {
                for (let i=1; i<=numPages; i++) {
                    result['page'+i] = this.template['page'+i].map(i => i.export());
                }
            }
        }

        return result;
    }

    import(content) {
        this.template = {page1:[]};
        this.renderTarget.map(r => r && (r.innerHTML = ''));

        let numPages = 1;

        if (content.length) {
            for (let item of content) {
                this.template['page1'].push(this.factoryField(item, 0));
            }
        } else {
            numPages = Object.keys(content).length;
            
            if (numPages > 0) {
                this.template = {...content};
            } else {
                numPages=1;
            }

            for(let p=1; p<=numPages; p++) {
                this.template['page'+p] = this.template['page'+p].map(i => this.factoryField(i, p-1))
            }
        }

        let minPages = this.getMinNumPages();

        this.onModifyContent({minPages});
        this.pageChangeCallback(numPages);
        
        this.render();
    }

    reimport() {
        this.import(this.template);
    }

    mouseDown(e, col, row, page=0) {
        this.mouseState[0] = 1;
        this.page = page;

        if (!this.tool) {
            this.seekItemToSelect(col, row, page);

            if (this.selectedItem) {
                let selected = this.getSelectedItem(page);
                this.offesetCell = [selected.x - col, selected.y - row];
            }
            return;
        }

        this.startCell = [col, row];
        this.endCell = [col, row];
        this.currentCell = [col, row];

        switch(this.tool) {
            case FieldTools.TEXT:
                this.currentField = new TextField(this.startCell);
                break;
            case FieldTools.TEXTSOLO:
                this.currentField = new TextSoloField(this.startCell);
                break;
            case FieldTools.LABEL:
                this.currentField = new Label(this.startCell);
                break;
            case FieldTools.TEXT_INT:
                this.currentField = new TextInt(this.startCell);
                break;
            case FieldTools.TEXT_FLOAT:
                this.currentField = new TextFloat(this.startCell);
                break;
            case FieldTools.TEXTAREA:
                this.currentField = new TextArea(this.startCell);
                break;
            case FieldTools.SELECT:
                this.currentField = new Select(this.startCell);
                break;
            case FieldTools.CHECKBOX:
                this.currentField = new Checkbox(this.startCell);
                break;
            case FieldTools.GROUP:
                this.currentField = new Group(this.startCell);
                break;
            case FieldTools.IMAGE:
                this.currentField = new Image(this.startCell);
                break;
            default:
                break;
        }

        this.currentField.setManager(this);
        this.currentField.setStartPoint(this.startCell);
        this.currentField.setEndPoint(this.startCell);
        this.currentField.setProps(this.fieldProps);
        this.currentField.setRunMacroCallback(()=>this.onAfterMacroRoll());
        this.currentField.create(this.renderTarget[this.page || 0], {onBlur: () => {this.buildSheet()}, unit: this.emUnit});

        this.fieldProps = this.currentField.getProps();
        this.render();

    }

    mouseMove(e, col, row, page=1) {
        if (!this.mouseState[0]) {
            return;
        }

        if (!this.tool) {
            let selected = this.getSelectedItem(page);
            if (selected) {
                selected.setX(Math.max(0, col + this.offesetCell[0]));
                selected.setY(Math.max(0, row + this.offesetCell[1]));
                selected.calculatePoints();

                this.fieldProps = selected.getProps();
                this.propagateSelection();
            }
        } else {
            this.endCell = [col, row];
            if (this.currentField !== null) {
                this.endCell = this.endCell.map((e,i) => e < this.startCell[i] ? e : e+1);
                this.currentField.setEndPoint(this.endCell);
                this.fieldProps = this.currentField.getProps();
            }
        }

        if (this.currentCell[0] !== col || this.currentCell[1] !== row) {
            this.currentCell = [col, row];
            this.render();
        }
    }

    mouseUp() {
        this.mouseState[0] = 0;
        this.exported(this.export());
        
        if (!this.tool || !this.currentField) {
            return;
        }

        this.fieldProps = this.currentField.getProps();

        this.template['page'+(this.page+1)].push(this.currentField);
        this.selectedItem = this.currentField.id;
        this.currentField = null;

        let minPages = this.getMinNumPages();

        this.onModifyContent({minPages});

        this.propagateSelection();
        this.render();
    }

    renderFields () {
        for (let page in this.template) {
            const pageNum = parseInt(page.substring(4)) - 1;
            if (this.template[page].length && this.renderTarget[pageNum]) {
                let options = this.factoryItemOptions();
                this.template[page].map(t => t.update(options));
            }
        }
    }

    renderCurrent() {
        if (this.currentField !== null) {
            let options = {
                unit: this.emUnit,
                highlight: true
            };
            this.currentField.update(options);
        }
    }

    render() {
        this.renderFields();
        this.renderCurrent();
    }


}